import React, { useEffect, useState } from 'react';
import { VERIFICATION } from '../global/Server/endpoints';
import './ConsentPage.css';

/**
 * Public consent page (NDS Phone Verification, Flow 2). The customer opens
 * this on their OWN phone via the SMS link (nuvesystem.com/v/<token>). They
 * review + accept consent; only then is their 6-digit check-in code revealed
 * on this page for them to read out to the employee.
 *
 * No authentication — the one-time consentToken in the URL is the access
 * secret. Calls the server-side permitAll endpoints directly.
 */
const ConsentPage = (props) => {
  const token = props.match?.params?.token;

  const [status, setStatus] = useState('loading'); // loading | ready | accepted | error
  const [phoneMasked, setPhoneMasked] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [openCard, setOpenCard] = useState(null); // 'privacy' | 'marketing' | null
  const [privacy, setPrivacy] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [code, setCode] = useState('');

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setStatus('error');
      setErrorMsg('This link is invalid.');
      return;
    }
    fetch(`${VERIFICATION.CONSENT_DETAILS}?token=${encodeURIComponent(token)}`)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        if (data.success) {
          setPhoneMasked(data.phoneMasked || '');
          setStatus('ready');
        } else {
          setStatus('error');
          setErrorMsg(
            data.message ||
              (data.expired
                ? 'This link has expired.'
                : 'This link is invalid or has already been used.')
          );
        }
      })
      .catch(() => {
        if (cancelled) return;
        setStatus('error');
        setErrorMsg('Something went wrong. Please try again.');
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const accept = () => {
    if (!privacy || submitting) return;
    setSubmitting(true);
    fetch(VERIFICATION.CONSENT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        consentToken: token,
        privacyAccepted: privacy,
        marketingConsent: marketing,
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success && data.code) {
          setCode(data.code);
          setStatus('accepted');
        } else {
          setStatus('error');
          setErrorMsg(data.message || 'Unable to complete. Please try again.');
        }
      })
      .catch(() => {
        setStatus('error');
        setErrorMsg('Something went wrong. Please try again.');
      })
      .finally(() => setSubmitting(false));
  };

  const renderCard = (id, icon, title, body, checkboxText, checked, onToggle) => (
    <div className={`v-card${checked ? ' ok' : ''}${openCard === id ? ' open' : ''}`}>
      <div
        className="v-chead"
        onClick={() => setOpenCard(openCard === id ? null : id)}
      >
        <span className="v-ci">{icon}</span>
        <span className="v-ct">{title}</span>
        <span className="v-chev">▼</span>
      </div>
      <div className="v-cbody">{body}</div>
      <div
        className="v-chk"
        onClick={(e) => {
          e.stopPropagation();
          onToggle(!checked);
        }}
      >
        <div className="v-box">{checked ? <span role="img" aria-label="checked">✓</span> : ''}</div>
        <span>{checkboxText}</span>
      </div>
    </div>
  );

  return (
    <div className="v-page">
      <div className="v-device">
        {status === 'loading' && (
          <div className="v-center v-pad">
            <div className="v-spinner" />
            <div className="v-sub">Loading…</div>
          </div>
        )}

        {status === 'error' && (
          <div className="v-center v-pad">
            <div className="v-ico v-ico-err">!</div>
            <div className="v-h2">Link Unavailable</div>
            <div className="v-sub">{errorMsg}</div>
          </div>
        )}

        {status === 'ready' && (
          <div className="v-pad">
            <div className="v-center">
              <div className="v-ico">
                <span role="img" aria-label="lock">🔒</span>
              </div>
            </div>
            <div className="v-h2">Activate Your Membership</div>
            <div className="v-sub">
              Review and accept to finish
              {phoneMasked ? ` · ${phoneMasked}` : ''}
            </div>

            {renderCard(
              'privacy',
              <span role="img" aria-label="phone">📱</span>,
              'Phone Verification',
              <>
                <p>
                  <b>Why We Need Your Phone Number</b>
                </p>
                <p>
                  Your phone number serves as your unique membership identifier
                  within NuVe System.
                </p>
                <p>
                  When checking in, you may receive a one-time verification code
                  (OTP) via SMS to confirm your identity and access your
                  membership account.
                </p>
                <p>
                  By continuing, you acknowledge and agree to the use of your
                  phone number for identity verification, account access, and
                  membership management purposes.
                </p>
              </>,
              'I understand my phone number will be used for membership verification, account access, and membership management within NuVe System.',
              privacy,
              setPrivacy
            )}

            {renderCard(
              'marketing',
              <span role="img" aria-label="gift">🎁</span>,
              'Member Benefits & Promotions',
              <>
                <p>
                  You may receive promotional offers, special event
                  announcements, loyalty program updates, membership rewards,
                  exclusive member benefits, business updates, and SMS
                  notifications.
                </p>
                <p>
                  Message and data rates may apply. Your consent to receive
                  promotional messages is not required to use NuVe System.
                </p>
                <p>
                  You may opt out at any time by texting STOP to any promotional
                  SMS message.
                </p>
              </>,
              'I agree to receive promotional messages, special offers, loyalty rewards, membership benefits, and business updates via SMS. I understand that I can opt out at any time by texting STOP.',
              marketing,
              setMarketing
            )}

            <button
              className={`v-btn${!privacy || submitting ? ' dis' : ''}`}
              onClick={accept}
              disabled={!privacy || submitting}
            >
              {submitting ? 'Please wait…' : 'Accept & Continue'}
            </button>
          </div>
        )}

        {status === 'accepted' && (
          <div className="v-pad">
            <div className="v-okrow">
              <div className="v-sc">
                <span role="img" aria-label="success">✓</span>
              </div>
              <div>
                <b>You're all set!</b>
                <div className="v-okhint">
                  Here is your check-in code — tell it to the employee.
                </div>
              </div>
            </div>
            <div className="v-code-reveal">{code}</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConsentPage;
